// SidebarChat.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import HighLogo from "../HighLogo";
import logo from "../../assets/logo.png";
import { Bell } from "lucide-react";
import NotificationSign from "../components/NotificationSign";
import { useTranslation } from "react-i18next";      
import { formatChatTimestamp } from "../../shared/chat/chatActiveChats";
           
export type ChatUser = {
  _id: string;
  profilename: string;
  lastMessage?: string;
  picture?: string;
  unread?: number;
  lastMessageType?: "text" | "image" | "video" | "audio" | "media";
  lastMessageAt?: string | number | Date;
};     
  
type SidebarChatProps = {
  search: string;
  onSearchChange: (value: string) => void;

  suggestions: ChatUser[];
  onSelectSuggestion: (u: ChatUser) => void;

  activeChats: ChatUser[];
  selectedUserId?: string | null;
  onSelectChat: (u: ChatUser) => void;

  onDeleteChat: (chatUserId: string) => void;
};

type MediaLabels = {
  image: string;
  video: string;
  audio: string;
  media: string;
};


const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const toAbsUrl = (p?: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${String(p).replace(/^\/+/, '')}`;
};

const getPreview = (
  user: Pick<ChatUser, "lastMessage" | "lastMessageType">,
  labels: MediaLabels,
  max = 70
) => {
  const t = user.lastMessageType || "text";
  if (t !== "text") {
    if (t === "image") return labels.image;
    if (t === "video") return labels.video;
    if (t === "audio") return labels.audio;
    return labels.media;
  }
  const s = user.lastMessage || "";
  return s ? s.replace(/\s+/g, " ").trim().slice(0, max) : "";
};


function Avatar({
  picture,
  name,
  size = 55,
}: {
  picture?: string;
  name?: string;
  size?: number;
}) {
  const [imgOk, setImgOk] = React.useState(true);
  const src = toAbsUrl(picture);
  const showImg = !!src && imgOk;

  // reset error state when picture changes
  React.useEffect(() => {
    setImgOk(true);
  }, [src]);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: showImg ? "transparent" : "rgb(38, 29, 162)",
        overflow: "hidden",
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 800,
        fontSize: 22,
      }}
      aria-label={name || "User"}
      title={name || "User"}
    >
      {showImg ? (
        <img
          src={src}
          alt={name || "User"}
          width={size}
          height={size}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={() => setImgOk(false)}
        />
      ) : (
        "👻"
      )}
    </div>
  );
}


const SidebarChat: React.FC<SidebarChatProps> = ({
  search,
  onSearchChange,
  suggestions,
  onSelectSuggestion,
  activeChats,
  selectedUserId,
  onSelectChat,
  onDeleteChat,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [menuOpenId, setMenuOpenId] = React.useState<string | null>(null);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  const [hiddenChatIds, setHiddenChatIds] = React.useState<Set<string>>(new Set());
  const visibleActiveChats = activeChats.filter((c) => !hiddenChatIds.has(c._id));

  const unhideChat = React.useCallback((id: string) => {
    setHiddenChatIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const mediaLabels: MediaLabels = {
    image: t("sidebarChat.preview.image"),
    video: t("sidebarChat.preview.video"),
    audio: t("sidebarChat.preview.audio"),
    media: t("sidebarChat.preview.media"),
  };

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const tEl = e.target as HTMLElement | null;
      if (!tEl) return;

      if (tEl.closest('[data-role="chat-menu"]')) return;
      if (tEl.closest('[data-role="chat-kebab"]')) return;
      if (tEl.closest('[data-role="suggestions-panel"]')) return;
      setMenuOpenId(null);
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <aside
      style={ui.wrap}
      ref={wrapRef}
      onMouseDownCapture={(e) => {
        const tEl = e.target as HTMLElement | null;
        if (!tEl) return;
        if (tEl.closest('[data-role="chat-kebab"]')) return;
        if (tEl.closest('[data-role="chat-menu"]')) return;
        if (tEl.closest('[data-role="suggestions-panel"]')) return;
        setMenuOpenId(null);
      }}
    >
      {/* Fixed area (does not scroll) */}
      <div style={ui.stickyHeader}>
        <div style={ui.headerBar}>
         <button
  type="button"
  style={ui.logoBtn}
  onClick={() => navigate("/gallery")}
  aria-label="Go to gallery"
>
  <HighLogo
    src={logo}
    style={ui.logo}
    padding={0}
    onClick={() => navigate("/gallery")}
  />
</button>

          <div style={ui.headerRight}>
  <NotificationSign
    to="/notifications"
    icon={<Bell size={21} color="#ff8c00" strokeWidth={2.2} />}
  />
</div>
        </div>

        <button type="button" style={ui.backButton} onClick={() => navigate("/gallery")}>
          ← {t("sidebarChat.actions.backToGallery")} 🖼
        </button>

        <div style={ui.searchWrap}>
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("sidebarChat.search.placeholder")}
            style={ui.searchInput}
          />

          {Boolean(search.trim()) && suggestions.length > 0 && (
            <div style={ui.suggestionsPanel} data-role="suggestions-panel">
              {suggestions.map((u) => (
  <div
    key={u._id}
    style={ui.suggestionItem}
    onClick={() => {
      unhideChat(u._id);
      onSelectSuggestion(u);
    }}
  >
    <Avatar picture={u.picture} name={u.profilename} size={36} />
    <div style={ui.suggestionTextWrap}>
      <div style={ui.suggestionName}>{u.profilename}</div>
    </div>
  </div>
))}

            </div>
          )}
        </div>
      </div>

      {/* Scroll area */}
      <div style={ui.sidebarBody}>
        <h3 style={ui.listHeader}>{t("sidebarChat.list.activeTitle")}</h3>

        <div style={ui.chatList}>
          {visibleActiveChats.map((chatUser) => {
            const tsLabel = formatChatTimestamp(chatUser.lastMessageAt);
            const active = selectedUserId === chatUser._id;
            const open = menuOpenId === chatUser._id;

            return (
              <div
                key={chatUser._id}
                style={ui.chatListItem(active)}
                onClick={() => {
                  setMenuOpenId(null);
                  unhideChat(chatUser._id);
                  onSelectChat(chatUser);
                }}
                title={chatUser.lastMessage || ""}
              >
                <div style={ui.itemMain}>
  <Avatar picture={chatUser.picture} name={chatUser.profilename} size={44} />

  <div style={ui.itemLeft}>
    <span style={ui.contactName}>{chatUser.profilename}</span>
    <span style={ui.lastMessage(active)}>{getPreview(chatUser, mediaLabels)}</span>
  </div>
</div>
            
                <div style={ui.itemRight}>
                  {typeof chatUser.unread === "number" && chatUser.unread > 0 && (
                    <span
                      style={ui.unreadBadge}
                      aria-label={t("sidebarChat.unreadLabel", { count: chatUser.unread })}
                    >
                      {chatUser.unread > 99 ? "99+" : chatUser.unread}
                    </span>
                  )}
            
                  <button
                    type="button"
                    aria-label={t("sidebarChat.menu.moreOptionsAria")}
                    title={t("sidebarChat.menu.moreOptionsTitle")}
                    style={ui.dotBtnSmall}
                    data-role="chat-kebab"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId((prev) => (prev === chatUser._id ? null : chatUser._id));
                    }}
                  >
                    ⋯
                  </button>
                </div>
            
                {/* pinned timestamp (always visible) */}
                <div style={ui.timeStampWrap}>
        <span style={ui.timeStamp}>{tsLabel}</span>
      </div>
            
                {open && (
                  <div
                    style={ui.itemMenu}
                    data-role="chat-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      style={ui.itemMenuItem}
                      onClick={() => {
                        setHiddenChatIds((prev) => {
                          const next = new Set(prev);
                          next.add(chatUser._id);
                          return next;
                        });
                        setMenuOpenId(null);
                      }}
                    >
                      {t("sidebarChat.menu.hideChat")}
                    </button>
            
                    <button
                      type="button"
                      style={ui.itemMenuItemDanger}
                      onClick={() => {
                        onDeleteChat(chatUser._id);
                        setMenuOpenId(null);
                      }}
                    >
                      {t("sidebarChat.menu.deleteChat")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default SidebarChat;

/* ---------------- styles ---------------- */
const ui = {
  wrap: { 
    display: "flex",
    flexDirection: "column",
    width: 360,
    marginLeft: -14,
    minWidth: 320,
    height: "100%",
    position: "relative",
    overflow: "hidden",
    marginTop: '7px',
  } as React.CSSProperties,

  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "inherit",
    padding: "14px 14px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  } as React.CSSProperties,

  headerBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
    marginLeft: -0.5,
  } as React.CSSProperties,

  logoBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    border: "none",
    background: "transparent",
    padding: 0,
    marginLeft: '-27px',
    cursor: "pointer",
  } as React.CSSProperties,

  logo: {
    width: 166,
    marginTop: -0.8,
    height: "auto",
    display: "block",
  } as React.CSSProperties,

  headerRight: {
    display: "flex",
    marginTop: -10,
    marginLeft: '28px',

  } as React.CSSProperties,

  backButton: {
    width: "60%",
    marginTop: 6,
    marginBottom: 24,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    cursor: "pointer",
    textAlign: "left",
    color: "#ddd",
    fontWeight: 600,
  } as React.CSSProperties,

  searchWrap: {
    position: "relative",
  } as React.CSSProperties,

  searchInput: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #333",
    outline: "none",
    background: "#111",
    color: "#ddd",
    boxSizing: "border-box",
  } as React.CSSProperties,
  suggestionsPanel: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    marginTop: 6,
    background: "#111",
    border: "1px solid #2a2a2a",
    borderRadius: 12,
    padding: 8,
    zIndex: 3000,
    maxHeight: 420,              // ✅ taller
    overflowY: "auto",
    boxShadow: "0 12px 24px rgba(0,0,0,.45)",
  } as React.CSSProperties,
  

  suggestionItem: {
    padding: "10px 10px",
    cursor: "pointer",
    background: "#111",
    borderRadius: 12,
    border: "1px solid #2a2a2a",
    marginBottom: 8,
    color: "#ddd",
    display: "flex",            // ✅ avatar + text row
    alignItems: "center",
    gap: 10,
  } as React.CSSProperties,
  
  suggestionTextWrap: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  } as React.CSSProperties,
  
  suggestionName: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as React.CSSProperties,
  
  sidebarBody: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: "10px 14px 14px",
  } as React.CSSProperties,

  listHeader: {
    marginTop: 8,
    marginBottom: 10,
    color: "#9aa0a6",
    fontWeight: 600,
    letterSpacing: 0.2,
    fontSize: 14,
  } as React.CSSProperties,

  chatList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: 4,
  } as React.CSSProperties,

  chatListItem: (active: boolean) =>
    ({
      position: "relative",
      width: "100%",
      padding: "8px 74px 12px 12px", // ⬅ more right space for unread/kebab + timestamp
      cursor: "pointer",
      background: active ? "#5b21b6" : "#111",
      color: active ? "#fff" : "#ddd",
      borderRadius: 12,
      border: active ? "1px solid #4c1d95" : "1px solid #2a2a2a",
  
      display: "flex",              // ⬅ simpler + no row clipping
      flexDirection: "column",
      justifyContent: "center",
      gap: 6,     
  
      marginBottom: 8,
      minHeight: 64,                // ⬅ enough space for 2 lines + timestamp
      boxSizing: "border-box",
      overflow: "visible",
           // ⬅ prevents weird overlaps
    } as React.CSSProperties),
    
    timeStampWrap: {
      position: "absolute",
      right: 12,
      bottom: 3,
      pointerEvents: "none", // IMPORTANT: never blocks scrolling/taps
    } as React.CSSProperties,

    timeStamp: {
      fontSize: 10,
      opacity: 0.75,
      lineHeight: "12px",
      whiteSpace: "nowrap",
    } as React.CSSProperties,
    
  itemLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  } as React.CSSProperties,

  itemRight: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,

  contactName: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  } as React.CSSProperties,

  lastMessage: (active: boolean) =>
    ({
      fontSize: "0.85em",
      marginTop: 3,
      color: active ? "rgba(255,255,255,.85)" : "#9aa0a6",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      maxWidth: "100%",
    } as React.CSSProperties),

  dotBtnSmall: {
    width: 28,
    height: 28,
    marginTop: -7,
    background: "transparent",
    border: "none",
    color: "inherit",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    borderRadius: 8,
  } as React.CSSProperties,

  unreadBadge: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 9999,
    background: "#1d93e1",
    color: "#fff",
    fontSize: 12,
    lineHeight: "20px",
    textAlign: "center",
    fontWeight: 800,
    boxShadow: "0 1px 0 rgba(0,0,0,.4)",
  } as React.CSSProperties,
  itemMenu: {
    position: "absolute",
    top: -2,
    right: 40,              // ✅ move beside the dots
    background: "#1b1b1b",
    color: "#ddd",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    padding: 6,
    minWidth: 160,
    zIndex: 9999,           // ✅ stay above other items
    boxShadow: "0 8px 18px rgba(0,0,0,.35)",
  } as React.CSSProperties,
  

  itemMenuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "5px 7px",
    background: "transparent",
    border: "none",
    color: "#ddd",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,
 
  itemMenuItemDanger: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "5px 7px",
    background: "transparent",
    border: "none",
    color: "#ff6b6b",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,

  itemMain: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  } as React.CSSProperties,
};
   